// src/components/merchant/MerchantLayout.js
import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  FiPackage,
  FiLogOut,
  FiMenu,
  FiX,
  FiPlus
} from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';

const MerchantLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = () => {
    try {
      if (typeof logout === 'function') logout();
    } catch (err) {}
    navigate('/login');
  };

  const menuItems = [
    { path: '/merchant/deals', icon: FiPackage, label: 'Mes Deals' }
  ];

  const isActive = (path, exact = false) =>
    exact ? location.pathname === path : location.pathname.startsWith(path);

  const initials =
    `${(user?.firstName?.[0] || '')}${user?.lastName?.[0] || ''}` || 'U';

  return (
    <div className="flex h-screen bg-gray-100">
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between h-16 px-6 border-b">
            <Link to="/merchant/deals" className="flex items-center">
              <span className="text-2xl font-bold text-primary-600">
                MAXULA
              </span>
              <span className="ml-2 text-sm text-gray-600">Merchant</span>
            </Link>

            <button
              type="button"
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-gray-600 hover:text-gray-900"
              aria-label="Fermer le menu"
            >
              <FiX size={24} />
            </button>
          </div>

          <div className="px-6 py-4 border-b bg-gradient-to-r from-primary-50 to-blue-50">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-primary-600 rounded-full flex items-center justify-center text-white font-semibold text-lg">
                {initials}
              </div>
              <div className="ml-3">
                <p className="text-sm font-semibold text-gray-900">
                  {user?.firstName || 'Utilisateur'} {user?.lastName || ''}
                </p>
                <p className="text-xs text-gray-600">{user?.email || ''}</p>
              </div>
            </div>
          </div>

          <div className="px-6 py-4">
            <Link
              to="/merchant/deals/new"
              className="flex items-center justify-center w-full px-4 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors shadow-sm"
              onClick={() => setSidebarOpen(false)}
            >
              <FiPlus className="mr-2" />
              Créer un Deal
            </Link>
          </div>

          <nav
            className="flex-1 px-4 py-4 space-y-1 overflow-y-auto"
            aria-label="Navigation marchand"
          >
            {menuItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                  isActive(item.path, item.exact)
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                aria-current={isActive(item.path, item.exact) ? 'page' : undefined}
              >
                <item.icon className="mr-3" size={20} aria-hidden="true" />
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="p-4 border-t">
            <button
              type="button"
              onClick={handleLogout}
              className="flex items-center w-full px-4 py-3 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <FiLogOut className="mr-3" size={20} />
              Déconnexion
            </button>
          </div>
        </div>
      </aside>

      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white shadow-sm border-b h-16 flex items-center justify-between px-6">
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden text-gray-600 hover:text-gray-900"
            aria-label="Ouvrir le menu"
          >
            <FiMenu size={24} />
          </button>

          <div className="flex items-center space-x-4" />
        </header>

        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-100">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default MerchantLayout;
